import { ArrowDropDown, Search } from "@mui/icons-material";
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Divider, Menu, OutlinedInput } from "@mui/material";
import { VariableSizeList as List } from "react-window";
import CustomCheckbox from "components/Checkbox";
import CustomOutlinedInput from "components/CustomOutlinedInput/CustomOutlinedInput";
const DEFAULT_ROW_HEIGHT = 34;
const MAX_VISIBLE_ITEMS = 8;
const LIST_WIDTH = 300;

const MultiSelectDropdown = ({
  label,
  placeholder,
  helperText,
  items,
  searchEnabled = false,
  onChange = () => {},
  selectedItems = [],
  error,
  onBlur = () => {},
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorEl = React.useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [allAndSelectedItems, setAllAndSelectedItems] = useState(items);

  // Synchronise l’état interne avec les éléments déjà sélectionnés (par leur libellé "name")
  useEffect(() => {
    const selectedSet = new Set((selectedItems || []).map((s) => String(s)));
    const withSelection = (items || []).map((item) => ({
      ...item,
      isSelected: selectedSet.has(String(item.name)),
    }));
    setAllAndSelectedItems(withSelection);
  }, [menuOpen, items, selectedItems]);

  const filteredItems = useMemo(
    () =>
      allAndSelectedItems.filter((item) =>
        [item.name]
          .filter(Boolean)
          .some((field) =>
            field.toLowerCase().includes(searchTerm.toLowerCase())
          )
      ),
    [searchTerm, allAndSelectedItems]
  );
  // Virtualized row renderer
  const Row = ({ index, style }) => {
    const item = filteredItems[index];
    return (
      <li
        style={{
          ...style,

          paddingBlock: "10px",
          height: style.height,
          width: "100%", // Fix width to 100% to avoid horizontal scroll
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          background: "#fff", // Remove alternate background
        }}
      >
        <CustomCheckbox
          fontSize="12px"
          size={"small"}
          checked={item?.isSelected}
          title={item?.label || item?.name}
          onChange={() => handleCheckboxChange(item)}
        />
      </li>
    );
  };
  // Dynamic row height calculation (can be customized per item)
  const getRowHeight = useCallback(
    (index) => {
      const item = filteredItems[index];
      const label = item?.label || item?.name || item?.hotel_name || "";
      return label.length > 30 ? DEFAULT_ROW_HEIGHT + 12 : DEFAULT_ROW_HEIGHT;
    },
    [filteredItems]
  );
  const handleCheckboxChange = (selectedItem) => {
    // Update the selected state for the clicked item
    const updatedItems = allAndSelectedItems.map((item) =>
      item.id === selectedItem.id ? { ...item, isSelected: !item.isSelected } : item
    );
    setAllAndSelectedItems(updatedItems);
    onChange(updatedItems);
  };
  return (
    <>
      <div ref={anchorEl}>
        <CustomOutlinedInput
          minWidth="310px"
          autoComplete="off"
          placeholder={placeholder}
          type={"text"}
          readOnly
          value={selectedItems.length > 0 ? selectedItems.length <=2 ? selectedItems.join(", ") : `${selectedItems.slice(0,2).join(", ")} and ${selectedItems.length - 2} others selected` : ""}
          sx={{
            fontSize: "12px",
            fontWeight: "400",
            paddingLeft: "4px",
          }}
          endAdornment={<ArrowDropDown sx={{ color: "#BAB7B7" }} />}
          onClick={(e) => {
            setMenuOpen(true);
          }}
          helperText={error ?? helperText}
          error={Boolean(error)}
          InputProps={{
            sx: {
              width: "360px",
              maxWidth: "360px",
              height: "28px",
              backgroundColor: "white",
            },
          }}
        />
      </div>
      <Menu
        sx={{ padding: 0 }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            marginTop: "-20px",
            maxWidth: LIST_WIDTH + 14,
            minWidth: LIST_WIDTH,
          },
        }}
        anchorEl={anchorEl.current}
        open={menuOpen}
        onClose={() => {
          onBlur();
          setMenuOpen(false)
        }}
      >
        <div
          aria-disabled
          style={{
            paddingInline: "17px",
            paddingTop: "0px",
            paddingBottom: "7px",
            minWidth: LIST_WIDTH,
          }}
        >
          {searchEnabled && (
            <OutlinedInput
              sx={{
                borderRadius: "10px",
                borderColor: "#CADEDD",
                width: LIST_WIDTH,
                height: "28px",
                fontSize: "12px",
                fontWeight: "400",
                fontFamily: "inter",
              }}
              size="small"
              placeholder="Search"
              startAdornment={
                <Search sx={{ color: "#CADEDD", fontSize: "18px" }} />
              }
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
          <div style={{ display: "flex", marginTop: "5px" }}>
            <div style={{ width: "100%" }}>
              <ul
                style={{
                  listStyleType: "none",
                  marginBlock: 0,
                  padding: 0,
                  paddingLeft: "4px",
                  width: "100%",
                  maxWidth: LIST_WIDTH,
                  maxHeight: `${DEFAULT_ROW_HEIGHT * MAX_VISIBLE_ITEMS}px`,
                  overflowY: "auto",
                  overflowX: "hidden", // Prevent horizontal scroll
                }}
              >
                <List
                  height={
                    Math.min(filteredItems.length, MAX_VISIBLE_ITEMS) *
                    DEFAULT_ROW_HEIGHT
                  }
                  itemCount={filteredItems.length}
                  itemSize={getRowHeight}
                  width={"100%"}
                >
                  {Row}
                </List>
              </ul>
              <Divider/>
            </div>
          </div>
        </div>
      </Menu>
    </>
  );
};

export default MultiSelectDropdown;
